import React, { useState } from 'react';
import api from '../api/axios';

interface Question {
  questionNumber: number;
  questionId: number | null;
  source: string;
  questionText: string;
  optionA: string; optionB: string; optionC: string; optionD: string;
  correctOption: string; explanation: string;
}

type Screen = 'difficulty' | 'loading' | 'quiz' | 'result';
type Difficulty = 'EASY' | 'MEDIUM' | 'HARD';

interface SubjectQuizProps {
  kidId: number | string;
  subjectId: number;
  topic: { topicId: number; topicName: string };
  onClose: () => void;
}

/** A per-topic quiz runner for subjects, mirroring MathQuiz: difficulty → quiz → result. */
const SubjectQuiz: React.FC<SubjectQuizProps> = ({ kidId, subjectId, topic, onClose }) => {
  const [screen, setScreen] = useState<Screen>('difficulty');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [current, setCurrent] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [result, setResult] = useState<any>(null);
  const [difficulty, setDifficulty] = useState<Difficulty>('MEDIUM');
  const [error, setError] = useState('');

  const base = `/api/kids/${kidId}/subjects/${subjectId}/topics/${topic.topicId}/quiz`;

  const beginQuiz = async (selected: Difficulty) => {
    setDifficulty(selected);
    setScreen('loading');
    setAnswers({});
    setCurrent(0);
    setError('');
    try {
      const res = await api.get(`${base}?difficulty=${selected}`);
      if (!res.data || res.data.length === 0) {
        setError('This quiz isn’t ready yet. Topics need their questions prepared first.');
        setScreen('difficulty');
        return;
      }
      setQuestions(res.data);
      setStartTime(Date.now());
      setScreen('quiz');
    } catch {
      setError('Failed to load quiz. Please try again.');
      setScreen('difficulty');
    }
  };

  const selectAnswer = (option: string) => setAnswers((prev) => ({ ...prev, [current]: option }));

  const submitQuiz = async () => {
    const timeTaken = Math.round((Date.now() - startTime) / 1000);
    let score = 0;
    const answerDetails = questions.map((q, i) => {
      const selected = answers[i] || '';
      const correct = selected === q.correctOption;
      if (correct) score++;
      return {
        questionNumber: i + 1,
        questionId: q.questionId || null,
        source: q.source || 'DB',
        questionText: q.questionText,
        optionA: q.optionA, optionB: q.optionB, optionC: q.optionC, optionD: q.optionD,
        correctOption: q.correctOption,
        selectedOption: selected,
        correct,
        explanation: q.explanation || '',
      };
    });
    try {
      const res = await api.post(`${base}/submit`, {
        score, totalQuestions: questions.length, timeTaken, answers: answerDetails,
      });
      setResult({ ...res.data, questions, answers });
      setScreen('result');
    } catch {
      alert('Failed to submit. Please try again.');
    }
  };

  const reportQuestion = async (q: Question) => {
    const reason = window.prompt('What\'s wrong with this question? (optional)') ?? '';
    try {
      await api.post('/api/quiz/report', {
        source: 'SUBJECT', questionId: q.questionId, questionText: q.questionText, reason,
      });
      alert('Thanks — this question has been reported for review.');
    } catch {
      alert('Could not send the report. Please try again.');
    }
  };

  const percentage = result ? Math.round(result.percentage) : 0;
  const emoji = percentage >= 80 ? '🌟' : percentage >= 60 ? '😊' : '💪';

  if (screen === 'difficulty') return (
    <div className="kt-card p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-bold text-gray-800">{topic.topicName} — Quiz</h2>
        <button onClick={onClose} className="text-sm text-gray-500">✕ Close</button>
      </div>
      <p className="text-sm text-gray-500 mb-4">Choose a difficulty to start.</p>
      <div className="space-y-3">
        {[
          { level: 'EASY' as Difficulty, emoji: '🟢', label: 'Easy', desc: 'Simple questions to build confidence', color: 'border-green-400 hover:bg-green-50' },
          { level: 'MEDIUM' as Difficulty, emoji: '🟡', label: 'Medium', desc: 'Balanced mix of questions', color: 'border-yellow-400 hover:bg-yellow-50' },
          { level: 'HARD' as Difficulty, emoji: '🔴', label: 'Hard', desc: 'Challenging questions to push your skills', color: 'border-red-400 hover:bg-red-50' },
        ].map((d) => (
          <button key={d.level} onClick={() => beginQuiz(d.level)}
            className={`w-full bg-white border-2 ${d.color} rounded-2xl p-4 flex items-center gap-4 transition text-left`}>
            <span className="text-3xl">{d.emoji}</span>
            <div className="flex-1">
              <span className="font-bold text-gray-800">{d.label}</span>
              <p className="text-sm text-gray-500">{d.desc}</p>
            </div>
            <span className="text-gray-300 text-2xl">›</span>
          </button>
        ))}
      </div>
      {error && <p className="text-red-500 text-sm mt-3">{error}</p>}
    </div>
  );

  if (screen === 'loading') return (
    <div className="kt-card p-10 text-center">
      <div className="text-5xl mb-3 animate-bounce">📝</div>
      <p className="text-gray-600 font-medium">Loading your quiz…</p>
    </div>
  );

  if (screen === 'result') return (
    <div className="space-y-4">
      <div className="kt-card p-6 text-center">
        <div className="text-6xl mb-2">{emoji}</div>
        <h2 className="text-2xl font-bold text-gray-800">{percentage}%</h2>
        <p className="text-gray-500">{result.score} / {result.totalQuestions} correct</p>
        <p className="text-sm text-gray-400 mt-1">{topic.topicName}</p>
        <div className="w-full bg-gray-200 rounded-full h-3 mt-4">
          <div className="bg-pink-500 h-3 rounded-full transition-all" style={{ width: `${percentage}%` }} />
        </div>
      </div>
      <h3 className="font-semibold text-gray-700">Review Answers</h3>
      {result.questions.map((q: Question, i: number) => {
        const selected = result.answers[i];
        const correct = q.correctOption;
        const isRight = selected === correct;
        return (
          <div key={i} className={`kt-card p-4 border-l-4 ${isRight ? 'border-green-400' : 'border-red-400'}`}>
            <p className="font-medium text-gray-800 mb-2">
              {i + 1}. {q.questionText}
              <span className={`ml-2 text-xs font-semibold ${isRight ? 'text-green-600' : 'text-red-500'}`}>
                {isRight ? '✓ Correct' : selected ? '✗ Incorrect' : '— Skipped'}
              </span>
            </p>
            {['A', 'B', 'C', 'D'].map((opt) => (
              <div key={opt} className={`text-sm px-3 py-1 rounded-lg mb-1
                ${opt === correct ? 'bg-green-100 text-green-700 font-semibold'
                  : opt === selected && !isRight ? 'bg-red-100 text-red-600' : 'text-gray-600'}`}>
                {opt}. {(q as any)[`option${opt}`]}
                {opt === correct && <span className="ml-1 text-green-600">✓</span>}
              </div>
            ))}
            <div className="mt-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              <p className="text-xs font-semibold text-blue-700">
                Why {correct} is correct
              </p>
              <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap leading-relaxed">
                {q.explanation || 'The correct answer is option ' + correct + '.'}
              </p>
            </div>
            <button onClick={() => reportQuestion(q)}
              className="mt-2 text-xs text-gray-400 hover:text-red-500">
              ⚠ Report this question
            </button>
          </div>
        );
      })}
      <div className="flex gap-3">
        <button onClick={() => { setScreen('difficulty'); setResult(null); }}
          className="flex-1 border-2 border-pink-300 text-pink-600 py-3 rounded-xl font-semibold">
          Try Again
        </button>
        <button onClick={onClose} className="flex-1 bg-pink-500 text-white py-3 rounded-xl font-semibold">
          Done
        </button>
      </div>
    </div>
  );

  // screen === 'quiz'
  const q = questions[current];
  const allAnswered = Object.keys(answers).length === questions.length;
  return (
    <div className="kt-card p-5">
      <div className="flex items-center justify-between mb-2">
        <button onClick={onClose} className="text-sm text-gray-500">✕ Exit</button>
        <div className="text-center">
          <h2 className="font-display font-bold text-gray-800 leading-tight">{topic.topicName}</h2>
          <span className="text-xs text-gray-400">{difficulty}</span>
        </div>
        <span className="text-sm text-gray-500">{current + 1}/{questions.length}</span>
      </div>
      <div className="w-full bg-gray-200 h-1.5 rounded-full mb-4">
        <div className="bg-pink-500 h-1.5 rounded-full transition-all"
          style={{ width: `${((current + 1) / questions.length) * 100}%` }} />
      </div>
      <p className="font-semibold text-gray-800 text-lg mb-4">{q.questionText}</p>
      {['A', 'B', 'C', 'D'].map((opt) => (
        <button key={opt} onClick={() => selectAnswer(opt)}
          className={`w-full text-left px-4 py-3 rounded-xl mb-2 border-2 transition font-medium
            ${answers[current] === opt
              ? 'border-pink-500 bg-pink-50 text-pink-700'
              : 'border-gray-200 hover:border-pink-300 text-gray-700'}`}>
          <span className="font-bold mr-2">{opt}.</span>
          {(q as any)[`option${opt}`]}
        </button>
      ))}
      <div className="flex gap-3 mt-3">
        {current > 0 && (
          <button onClick={() => setCurrent((c) => c - 1)}
            className="flex-1 border-2 border-pink-300 text-pink-600 py-3 rounded-xl font-semibold">← Back</button>
        )}
        {current < questions.length - 1 ? (
          <button onClick={() => setCurrent((c) => c + 1)} disabled={!answers[current]}
            className="flex-1 bg-pink-500 text-white py-3 rounded-xl font-semibold disabled:opacity-40">Next →</button>
        ) : (
          <button onClick={submitQuiz} disabled={!allAnswered}
            className="flex-1 bg-green-500 text-white py-3 rounded-xl font-semibold disabled:opacity-40">Submit Quiz ✓</button>
        )}
      </div>
    </div>
  );
};

export default SubjectQuiz;
